import { codeInputCommonAttrs } from '../../common/$models/constants'
import { validationConfigs } from '../../common/modelDefinitions/validationConfigs'
import { displayModelNameWithLabel } from '../../common/utils'
import { addModelDoc } from '../../plugins/HelpDoc/coreDocsHelperFunctions'
import { ModelDef } from '../../types'

const colBehaviorTypeAndLabel = {
  direct: 'ダイレクト',
  func: '関数制御',
  // リレーション先照合
  // referenceToM2O: 'リレーション先照合',
  // passThrough: 'パススルー',
}
const modelName = 'dataImportSettings'

addModelDoc(
  modelName,
  'データインポート設定では、外部システムから出力されたCSVやExcelファイルを取り込む設定を作成可能です。定義後は、 <router-link to="/importDataFromExternal">データインポート実行</router-link> 画面より利用可能です。<br/>必要に応じて、Javascript関数による変換処理・カスタマイズが可能であるため、多彩なケースに対応可能です。',
)

const model: ModelDef = {
  tableName: modelName,
  tableLabel: 'データインポート設定',
  bulkControllable: true,
  tableComment: '',
  primaryKeyColType: 'UUID',
  defaultSort: {
    key: 'createdAt',
    order: 'desc',
  },
  formColumnGroups: {
    fields: {
      type: 'tab',
      groupKey: 'tab1',
      label: '取り込み設定',
      order: 1,
    },
    validate: {
      type: 'tab',
      groupKey: 'tab1',
      label: '入力値チェック設定',
      order: 2,
    },
    convertSettings: {
      type: 'tab',
      groupKey: 'tab1',
      label: '変換処理設定',
      order: 3,
    },
    advanced: {
      type: 'tab',
      groupKey: 'tab1',
      label: '高度な設定',
      order: 4,
    },
  },
  columns: {
    name: {
      label: 'インポート設定名',
      type: 'STRING',
      facet: true,
      unique: true,
      // editable: false,
      // editableOnCreate: true,
      searchable: true,
      // inputHelpText: '名称は後から変更することができませんのでご注意ください。',
      validate: {
        notEmpty: true,
      },
      inputAttrs: { wrapperClass: 'col-4' },
      afterComponent:
        '<span class="small text-muted" v-if="$parent.value">/importDataFromExternal/{{$parent.value}} <br/>で個別画面としてアクセス可能です。</span>',
      listItemAttrs: { class: { 'no-ellipsis': true } },
    },
    targetModelName: {
      type: 'STRING',
      label: 'インポート先モデル',
      // editable: false,
      // editableOnCreate: true,
      strictSelections: true,
      validate: {
        notEmpty: true,
      },
      selections: () => {
        return $core.$modelsLoader.modelNames
      },
      customLabel: (val, callerVueInstance, recordRoot) => {
        return displayModelNameWithLabel(val)
      },
      editCallback({ row, newValue, oldValue }) {
        if (newValue && newValue !== oldValue) {
          row.targetVirtualModelName = null // 排他制御
          if (!row.name) {
            row.name = displayModelNameWithLabel(newValue, false, true)
          }
        }
        return row
      },
      inputAttrs: { wrapperClass: 'col-4' },
    },
    targetVirtualModelName: {
      type: 'STRING',
      label: 'インポート先Virtualモデル',
      strictSelections: true,
      selections: () => {
        return Object.keys($core.$virtualModels)
      },
      customLabel: (val) => {
        const model = $core.$virtualModels[val]
        return model ? `${model.tableLabel} (${val})` : val
      },
      editCallback({ row, newValue, oldValue }) {
        if (newValue && newValue !== oldValue) {
          row.targetModelName = $core.$virtualModels[newValue]?.baseModel || null // 強制変更
          if (!row.name) {
            row.name = displayModelNameWithLabel(newValue, false, true)
          }
        }
        return row
      },
      inputAttrs: { wrapperClass: 'col-4' },
    },
    desc: {
      label: '管理用メモ',
      type: 'TEXT',
      inputAttrs: { wrapperClass: 'col-4' },
    },
    readRange: {
      groupKey: 'fields',
      label: '読み込み範囲',
      type: 'STRING',
      defaultValue: '1',
      virtualColumnOf: 'metaData',
      visibleOnIndex: false,
      inputHelpText:
        '読み込み範囲を指定します。例:<br/>開始行指定: <code>5</code> (数値のみ)<br/>範囲指定: <code>B6:AD9999</code>',
      validate: {
        // 数値 or 範囲指定値
        validRangeValue: (val) => {
          if (val) {
            if (/^\d+$/.test(val)) {
              const num = parseInt(val, 10)
              if (num < 1) {
                return '行番号は 1以上の整数で指定してください'
              }
              return // OK
            }
            // // 範囲指定値 (例: B6:AD99999)
            if (!$core.$exportAsExcel.validateExcelRangeValue(val)) {
              return '読み込み範囲が正しくありません。開始行数値 もしくは B6:AD9999 の Excel 範囲形式で指定してください'
            }
          }
        },
      },
    },
    pathThroughOriginalProps: {
      groupKey: 'fields',
      label: 'パススルー取込を実施',
      type: 'BOOLEAN',
      default: false,
      inputHelpText:
        '取込フィールドの設定に加えて、Excel列名(1列目)の項目名をそのまま取り込みます。 一覧表示の "Excelエクスポート" からダウンロードしたデータを変更して、そのまま取り込む際に便利です。',
    },
    disableImportIfHasValidationErrors: {
      groupKey: 'validate',
      label: '値チェックのエラーが1つでもある場合は 取込を禁止する',
      type: 'BOOLEAN',
      defaultValue: false,
      visibleOnIndex: false,
      virtualColumnOf: 'metaData',
      width: { xs: 48 },
    },
    allowMultipleSheetSelection: {
      groupKey: 'advanced',
      label: '複数シートの取込を許可',
      type: 'BOOLEAN',
      default: true,
      width: { xs: 48 },
      virtualColumnOf: 'metaData',
      inputHelpText:
        '取込時に Excelファイル内の複数シートを選択して 実行することが可能です。',
    },
    useXlsxParseOptionFunction: {
      groupKey: 'advanced',
      // TODO: 未実装 on ./importServiceWithDataImportSetting.ts
      label: 'Excel ファイルのパースオプションを利用する',
      type: 'BOOLEAN',
      virtualColumnOf: 'metaData',
      default: false,
      inputAttrs: { wrapperClass: 'col-2' },
      visibleOnIndex: false,
      editCallback({ row, newValue, oldValue }) {
        if (newValue !== oldValue && newValue && !row.dateConversionFormat) {
          row.dateConversionFormat = 'yyyy-mm-dd'
        }
        return row
      },
      width: { xs: 48 },
    },
    xlsxParseOptionFunction: {
      groupKey: 'advanced',
      label: 'Excel ファイルのパースオプション (関数)',
      type: 'TEXT',
      virtualColumnOf: 'metaData',
      inputComponent: 'CodeEditor',
      enableIf: { useXlsxParseOptionFunction: true },
      default: false,
      width: { xs: 48 },
      inputAttrs: {
        initCode: `/**
 * @typedef {Object} XlsxSheetToJsonOptions - xlsx (sheetjs) の sheet_to_json のオプション, 詳細は https://docs.sheetjs.com/docs/api/utilities/array を参照
 * @property {number|string} range 数値を指定した場合は 開始行番号, セル範囲文字列 (例: "D5:BC8000") を指定した場合は セル範囲
 * @property {string[]|'A'} header
 * @property {string} dateNF 日付形式を文字列に変換する場合に FMT 14 (Microsoft Excel の日付の形式コード) 形式で指定します。例: 'yyyy-mm-dd'
 * @property {string} defval null or 空の場合の値を設定します。例: "__空値です__"
 * @property {boolean} raw false を指定すると フォーマット済みの値を読み取ります (年月や、単位などを含める)
 * @property {boolean} blankrows 空行を含めるかどうか, false を指定すると 空行を含めません
 **/

/**
 * ## 取込ファイル読み取り時のオプション設定を定義する関数
 * - 読み取り範囲指定等が可能
 * - 複数シートを読み込む場合は 1シート毎に下記関数が実行されます
 * - 特殊なケースへの対応: 読み取りデータを 加工して 返却することも可能
 * 
 * @param {Object} args
 * @param {Object} args.workbook workbook SheetJs の workbook オブジェクト (Excel データ)
 * @param {string} args.sheetName 読み込み対象シート名
 * @param {string[]} args.selectedSheetNames 読み込み対象として選択された Excel シートの名称配列
 * @param {number} args.readingSheetIndex 読み込み中の対象シートのインデックス (複数読み込み時に, 0, 1, 2, ... と インデックスが増えていく)
 * @param {Record<string, any>[]} args.readResults すでに読み取った データ行の配列
 * @return {Promise<Partial<(XlsxSheetToJsonOptions & Record<string, any>) | {readResultData: Record<string, any>[]}>>} 返り値: 読み取りオプション または 読み取り済みデータ行の配列
 */
const optionConvertFunction = async ({
  workbook,
  sheetName,
  selectedSheetNames,
  readingSheetIndex,
  readResults
}) => {
  /** @type Partial<XlsxSheetToJsonOptions & Record<string, any>> */
  const readOptions = {}
  // readOptions.range = 5 // 5行目から読み込む
  // readOptions.range = 'B7:AH9999' // 範囲指定読み取り

  // ※ 読み取り範囲指定内の 1行目をヘッダーとして扱う場合は header は 指定する必要はありません
  // readOptions.header = ['品名', '数量', '単価', '金額'] // 1行の読み取り属性を Excelファイル内にヘッダ行がない場合などで利用
  // readOptions.header = 'A' // Excel の列のキー (A, B, C... AA, AB, AC... 等) を ヘッダーとして出力します

  // // 複雑なケースへの対応例: 読み込み対象シート内の データを探索して 範囲を抽出する 等といった場合, workbook.Sheets[sheetName] で データを参照可能
  // const sheet = workbook.Sheets[sheetName]
  // const data = $core.$exportAsExcel.sheetToJson(sheet, {
  //   header: 'A' // 列のキーを ヘッダーとして出力
  // })
  // // 結果 range を設定する
  // readOptions.range = detectedRange

  // // 複雑なケースへの対応例2: workbook およびその他データを加工して 読み取りデータそのものを 返却する
  // const someGeneratedData = readResults.map((row) => {
  //   return {
  //     ...row,
  //     // 加工処理
  //   }
  // })
  // // 読み取りオプションではなく、データそのものを返却する
  // return {
  //   readResultData: someGeneratedData
  // }

  return readOptions
}
return optionConvertFunction`,
      },
    },
    useDateConversion: {
      groupKey: 'advanced',
      // TODO: 未実装 on ./importServiceWithDataImportSetting.ts
      label: '日付変換を利用する',
      type: 'BOOLEAN',
      default: true,
      inputAttrs: { wrapperClass: 'col-2' },
      visibleOnIndex: false,
      editCallback({ row, newValue, oldValue }) {
        if (newValue !== oldValue && newValue && !row.dateConversionFormat) {
          row.dateConversionFormat = 'yyyy-mm-dd'
        }
        return row
      },
    },
    dateConversionFormat: {
      groupKey: 'advanced',
      // TODO: 未実装 on ./importServiceWithDataImportSetting.ts
      label: '日付変換フォーマット',
      type: 'STRING',
      enableIf: { useDateConversion: true },
      default: 'yyyy-mm-dd',
      visibleOnIndex: false,
    },
    useBeforeFormatFunction: {
      groupKey: 'advanced',
      label: '変換処理前関数(全件対象)を利用する',
      type: 'BOOLEAN',
      visibleOnIndex: false,
      inputAttrs: { wrapperClass: 'col-12' },
    },
    beforeFormatFunction: {
      groupKey: 'advanced',
      label: '変換処理前関数(全件対象)',
      type: 'TEXT',
      // afterComponent:
      //   '<div class="small text-muted">async ({<code>originalDataRows</code>, <code>instance</code>, <code>sourceData</code>, <code>sourceDataIndex</code>}): Promise => { (...); return originalDataRows } のコンテキストで実行されます。 変換処理前の 取込データを変更するには <code>originalDataRows</code> (配列) を書き換えてください。</div>',
      inputAttrs: {
        ...codeInputCommonAttrs,
        initCode: `/**
  * 変換処理前関数(全件対象)
  * - 変換処理を実施する前に 取込データを加工することが可能です
  * - 例:
  *   - 取込対象を フィルタリング (除外) する
  *   - 他のモデルからデータを照合・取得して 取込データを加工する
  *
  * @param {Object} args
  * @param {Record<string, any>[]} args.originalDataRows 取り込みデータ行
  * @param {ImportServiceWithDataImportSetting} args.instance データ取込実行サービスインスタンス
  * @returns {Promise<Record<string, any>[]>}
  */
const beforeFormatFunction = async ({ originalDataRows, instance }) => {
  // originalDataRows = originalDataRows.filter((row) => !isNaN(row['数量'])) // 数量が数値でない行を除外する例

  // originalDataRows = await Promise.all(originalDataRows.map(async (row, index) => {
  //   instance.displayErrorMessages.push(\`\${index + 1}行目の列 "ステータス" に不正な値が含まれています。\`) // エラーメッセージを出す例
  //   instance.displayWarningMessages.push(\`\${index + 1}行目の列 "ステータス" に不正な値が含まれています。\`) // 警告メッセージを出す例
  //   if (row['ステータス'] === '未完了') {
  //     row['ステータス'] = '未完了'
  //   }
  //   return row
  // }))
  return originalDataRows
}
return beforeFormatFunction`,
      },
      enableIf: { useBeforeFormatFunction: true },
      visibleOnIndex: false,
    },
    useAfterFormatFunction: {
      groupKey: 'advanced',
      label: '変換処理後関数(全件対象)を利用する',
      type: 'BOOLEAN',
      visibleOnIndex: false,
      inputAttrs: { wrapperClass: 'col-12' },
    },
    afterFormatFunction: {
      groupKey: 'advanced',
      label: '変換処理後関数(全件対象)',
      type: 'TEXT',
      // comment: 'async ({formattedDataRows}): Promise => { (...) } のコンテキストで実行されます。formattedDataRows のプロパティを設定、書き換えください。',
      // afterComponent:
      //   '<div class="small text-muted">async ({<code>formattedDataRows</code>, <code>instance</code>, <code>originalDataRows</code>}): Promise => { (...); return formattedDataRows } のコンテキストで実行されます。 変換処理結果を変更するには <code>formattedDataRows</code> (配列) を書き換えてください。</div>',
      inputAttrs: {
        ...codeInputCommonAttrs,
        initCode: `/**
  * 変換処理後関数(全件対象)
  * - 変換処理を実施した後に 取込データを加工することが可能です
  * - 例:
  *   - 他のモデルからデータを照合・取得して 取込データを加工する
  *
  * @param {Object} args
  * @param {Record<string, any>[]} args.formattedDataRows 変換処理完了後の行データ(配列)
  * @param {ImportServiceWithDataImportSetting} args.instance インポートサービスインスタンス
  * @param {Record<string, any>[]} args.originalDataRows 取り込みデータ行
  * @returns {Promise<Record<string, any>[]>}
  */
const afterFormatFunction = async ({
  formattedDataRows,
  instance,
  originalDataRows,
}) => {
  return formattedDataRows
}
return afterFormatFunction`,
      },
      enableIf: { useAfterFormatFunction: true },
      visibleOnIndex: false,
    },
    beforeEachRowConvertFunction: {
      groupKey: 'advanced',
      label: '1行単位での処理関数(変換前処理)',
      visibleOnIndex: false,
      type: 'TEXT',
      inputComponent: 'CodeEditor',
      inputAttrs: {
        ...codeInputCommonAttrs,
        initCode: `/**
  * 1行単位での処理関数(変換前処理)
  * - 取込処理を実施する際に 1行単位での変換処理を実施していく 最初に実行される処理
  * - 取込データ (Excel) 側の行 などを 引数として参照可能
  * - 変換後の行データ (output, オブジェクト) を 返します
  *
  * @param {Object} args
  * @param {Record<string, any>} args.row アウトプットとなる行データ (オブジェクト)
  * @param {Record<string, any>} args.sourceDataRow 取り込みデータ行 ()
  * @param {Record<string, any>[]} args.allSourceDataRows 全取り込みデータ行
  * @param {number} args.sourceDataRowIndex 取り込みデータ行のインデックス
  * @param {Record<string, any>[]} args.formattedRows 変換処理完了後の行データ(配列)
  * @returns {Promise<Record<string, any>>}
  */
const beforeEachRowConvertFunction = async ({
  row,
  sourceDataRow,
  allSourceDataRows,
  sourceDataRowIndex,
  formattedRows,
}) => {
  // 変換前処理の例
  // row.total_price = sourceDataRow['数量'] * (sourceDataRow['単価'] || 0) // セルの合計処理等
  // 特定の列が "空であれば 前の行の値を引き継ぐ" 挙動の例
  // if (!row['status'] && formattedRows[sourceDataRowIndex - 1]?.['status']) {
  //   row['status'] = formattedRows[sourceDataRowIndex - 1]['status']
  // }
  return row // 
}
return beforeEachRowConvertFunction  
`,
      },
    },
    excludeFields: {
      groupKey: 'advanced',
      enableIf: (row) => !!row.targetModelName,
      type: 'MULTISELECT',
      label: '取込対象外フィールド (保護フィールド)',
      visibleOnIndex: false,
      inputAttrs: { wrapperClass: 'col-12' },
      selections(record, currentValue, initialValue) {
        return [''].concat($core.$models[record.targetModelName]?.colNames || [])
      },
      inputHelpText:
        '指定したフィールドは取込時に更新されず、値が保護されます。各行の変換処理後に除外処理を実施します。',
    },
    useDirectBulkUpsertOnSave: {
      groupKey: 'advanced',
      label: '保存時に 簡易・高速一括保存処理 を利用する',
      type: 'BOOLEAN',
      default: false,
      visibleOnIndex: false,
      inputHelpText:
        'このオプションを選択した場合には 保存処理は高速化されますが、 <strong>リレーション先のレコードは保存されず、変更履歴は記録されなくなります</strong>。挙動が変わることに注意してください。',
      width: { xs: 48, sm: 24 },
      virtualColumnOf: 'metaData',
    },
    fields: {
      groupKey: 'fields',
      enableIf: (row) => !!row.targetModelName,
      label: '取り込み列毎の変換処理設定',
      type: 'ARRAY_OF_OBJECT',
      visibleOnIndex: false,
      inputAttrs: {
        wrapperClass: 'col-12',
      },
      minValueLength: 0,
      columns: {
        behavior: {
          label: '取込挙動',
          type: 'SELECT',
          strictSelections: true,
          selections: () => Object.keys(colBehaviorTypeAndLabel),
          customLabel: (val) => colBehaviorTypeAndLabel[val],
          default: 'direct',
          validate: { notEmpty: true },
          inputAttrs: { wrapperClass: 'col-2' },
        },
        dataCol: {
          type: 'STRING',
          label: '取り込みデータ列名',
          validate: { notEmpty: true },
          inputAttrs: { wrapperClass: 'col-2' },
        },
        tCol: {
          type: 'STRING',
          label: '保存先カラム名',
          validate: { notEmpty: true },
          width: {
            md: 12,
          },
          dynamicSelections: true,
          selections(record, currentValue, initialValue, recordRoot) {
            const modelName = recordRoot.targetModelName
            const virtualModelName = recordRoot.targetVirtualModelName

            if (!modelName && !virtualModelName) {
              return []
            }

            const isVirtual = !!virtualModelName
            const targetName = virtualModelName || modelName
            const model = isVirtual ? $core.$virtualModels[targetName] : $core.$models[targetName]

            if (!model) return []

            const virtualModel = isVirtual ? $core.$virtualModels[targetName] : null
            const columns = virtualModel?.columnsMergedWithBaseModel || model.columns
            const existingCols = (recordRoot.fields || []).map((f) => f.tCol)

            return [''].concat(
              Object.keys(columns).filter(
                (colName) => existingCols.indexOf(colName) === -1,
              ),
            )
          },
          customLabel: (val, callerVueInstance, recordRoot) => {
            if (!$core.$models[recordRoot.targetModelName]) {
              return ''
            }
            if ($core.$models[recordRoot.targetModelName].colLabels[val]) {
              return `${$core.$models[recordRoot.targetModelName].colLabels[val]} (${val})`
            }
            return val
          },
          inputAttrs: { wrapperClass: 'col-2' },
          enableIf: (row, parentRecord) => {
            return ['direct'].includes(row.behavior)
          },
        },
        shouldExtendPreviousRowValueIfEmpty: {
          label: '空値であれば前行の値を引き継ぐ',
          displayAsDetail: true,
          type: 'BOOLEAN',
          virtualColumnOf: 'metaData',
          default: false,
          width: { xs: 'max-content' },
          visibleOnIndex: false,
          inputHelpText: 'Excel にて セルの結合を多用している場合などで活用します。',
          enableIf: (row, parentRecord) => row.behavior !== 'func',
        },
        tFunc: {
          enableIf: (row, parentRecord) => row.behavior === 'func',
          label: '変換処理関数',
          type: 'TEXT',
          validate: { notEmpty: true },
          displayAsDetail: true,
          inputAttrs: {
            ...codeInputCommonAttrs,
            initCode: `/**
  * 取込データ変換処理関数
  *
  * @param {Object} args
  * @param {Record<string, any>} args.row 現在の変換中の行データ
  * @param {any} args.value 取込対象データ行の 対象の列の値
  * @param {Record<string, any>} args.sourceDataRow 取込対象データ行
  * @param {Record<string, any>[]} args.allSourceDataRows 全取込対象データ行
  * @param {number} args.sourceDataRowIndex 取り込みデータ行のインデックス
  * @param {Record<string, any>[]} args.formattedRows 変換処理中の行データ(配列)
  */
const fieldConvFunction = async ({
  value,
  row,
  sourceDataRow,
  allSourceDataRows,
  sourceDataRowIndex,
  formattedRows,
}) => {
  // // 例: ステータス値の変換処理 - 出荷日 が 入っているなら ステータス値を 完了 にする
  // if (sourceDataRow['出荷日']) {
  //   row.status = '完了'
  //   row.delivery_date = $core.$dayjs(sourceDataRow['出荷日']).format('YYYY-MM-DD')
  // }

  // // 例: 空値であれば前の行の値を引き継ぐ
  // row.order_number = sourceDataRow['注文番号'] || formattedRows[sourceDataRowIndex - 1]?.order_number || ''
  return row
}
return fieldConvFunction`,
          },
        },
      },
      validate: {
        // notEmpty: true,
        doNotDuplicateDataColOrTargetColName: (value, col, modelName, record) => {
          if (!value) {
            return false
          }
          // dataCol 重複
          const dupDataCols = value.filter(
            (x, i, self) => self.indexOf(x) !== self.lastIndexOf(x),
          )
          // tCol 重複
          const dupTCols = value.filter(
            (x, i, self) => self.indexOf(x) !== self.lastIndexOf(x),
          )
          if (dupDataCols.length || dupTCols.length) {
            return (
              `${
                dupDataCols.length
                  ? `取り込みデータ列名 "${dupDataCols.join('", "')}" が重複して設定されています。`
                  : ''
              }` +
              `${
                dupTCols.length
                  ? `保存先フィールド名 "${dupTCols.join('", "')}" が重複して設定されています。`
                  : ''
              }`
            )
          }
          return false
        },
      },
    },
    validationConfigsByImportColumns: {
      type: 'ARRAY_OF_OBJECT',
      virtualColumnOf: 'metaData',
      groupKey: 'validate',
      label: '保存先カラム毎のバリデーション設定',
      inputHelpText: '保存先カラム毎に入力値チェック設定を適用することができます。',
      width: { xs: 48 },
      minValueLength: 0,
      columns: {
        targetCol: {
          type: 'STRING',
          label: '保存先カラム名',
          validate: { notEmpty: true },
          selections(record, currentValue, initialValue, recordRoot) {
            return $core.$models[recordRoot.targetModelName].colNames
          },
          customLabel: (val, callerVueInstance, recordRoot) => {
            const label = $core.$models[recordRoot.targetModelName].colLabels[val]
            return label ? `${label} (${val})` : val
          },
        },
        useColumnDefValidation: {
          type: 'BOOLEAN',
          label:
            '保存先フィールドのカラム定義の入力値チェック設定 (バリデーション) を適用する',
          width: { md: 36 },
          enableIf: (row, recordRoot) => {
            return (
              row.targetCol &&
              Object.keys(
                $core.$models[recordRoot.targetModelName]?.columns[row.targetCol]
                  ?.validate || {},
              ).length
            )
          },
        },
        validationConfigs: {
          ...validationConfigs,
          groupKey: '',
          displayAsDetail: undefined,
          hideLabel: false,
          width: { xs: 48 },
        },
      },
    },
    // リレーション先への変換設定
    convertSettingsToRelationId: {
      type: 'ARRAY_OF_OBJECT',
      virtualColumnOf: 'metaData',
      label: 'リレーション先への変換設定',
      hideLabel: true,
      beforeComponents: [
        `<div class="small"><div class="mb-1">M2Oリレーション先への変換設定</div><HelpDoc>取込値を リレーション先の プライマリキー (id値) へ変換したい場合に、設定をすることができます。 <br/>例えば 『<code>"受注"</code> データをインポートする場合に、取り込み列 <code>"取引先コード"</code> の値を M2Oリレーション先のモデル <code>"取引先" (companies)</code> に登録されている <code>"取引先コード" (code)</code> カラムと照合して、正しくリレーションとして保存 (<code>id</code> へ 変換)』 する設定を記載可能です。</HelpDoc></div>`,
      ],
      groupKey: 'convertSettings',
      minValueLength: 0,
      columns: {
        /**
         * - columns
         *     - キーとなるカラム (複数)
         *     - 変換先モデル
         *     - 変換先値 格納カラム
         */
        relationModelName: {
          type: 'SELECT',
          label: '変換先モデル',
          selections: () => {
            return $core.$modelsLoader.modelNames
          },
          customLabel: (val, callerVueInstance, recordRoot) => {
            return displayModelNameWithLabel(val)
          },
        },
        relationIdCol: {
          type: 'STRING',
          label: 'リレーション先のプライマリキー (id) を保存するカラム名',
          validate: { notEmpty: true },
          // dynamicSelections: true,
          // selections(record, currentValue, initialValue, recordRoot, ) {
          //   return $core.$models[record.relationModelName].colNames
          // },
          width: { md: 24 },
        },
        targetCol: {
          type: 'ARRAY_OF_OBJECT',
          label: '取込値・照合条件マッピング設定',
          enableIf: (row) => !!row.relationModelName,
          inputHelpText:
            'キーとなる取り込み列と 変換先モデルのカラム値 の対応 を指定します。 (複数設定可能)',
          columns: {
            dataCol: {
              type: 'STRING',
              label: '取込データ列名',
              validate: { notEmpty: true },
              inputAttrs: { wrapperClass: 'col-2' },
              selections(record, currentValue, initialValue, recordRoot) {
                return [''].concat((recordRoot.fields || []).map((f) => f.dataCol))
              },
              inputHelpText: '直接指定も可能です。',
            },
            targetCol: {
              type: 'STRING',
              label: '変換先モデルの 検索・照合対象カラム',
              validate: { notEmpty: true },
              dynamicSelections: true,
              selections(
                record,
                currentValue,
                initialValue,
                recordRoot,
                callerVueInstance,
              ) {
                const parentRow = $core.$utils.findParentVueComponentByComponentName(
                  callerVueInstance,
                  'oneRowOfArrayOfObjectInput',
                  2,
                )?.record
                const relationModelName = parentRow?.relationModelName
                if (!relationModelName) {
                  return []
                }
                return $core.$models[relationModelName].colNames
              },
            },
            matchType: {
              type: 'SELECT',
              label: '照合方法',
              selections: () => [
                { value: '_eq', label: '完全一致' },
                { value: '_starts_with', label: '前方一致' },
                { value: '_ends_with', label: '後方一致' },
                { value: '_contains', label: '部分一致' },
              ],
              default: '_eq',
              inputAttrs: { wrapperClass: 'col-2' },
            },
          },
          width: { xs: 48 },
        },
      },
      width: { xs: 48 },
    },
    // カラム値による 重複判別の設定
    duplicateCheckSettings: {
      type: 'ARRAY_OF_OBJECT',
      virtualColumnOf: 'metaData',
      label: 'カラム値による重複判別の設定',
      hideLabel: true,
      beforeComponents: [
        `<div class="small"><div class="mb-1">カラム値による重複判別の設定</div><HelpDoc>取込値による重複判定 および 更新処理を行う場合に、設定をすることができます。設定内容に従って取込時に レコードを検索し、マッチした場合は プライマリキー (id) を取得して 取込値にセットします。 <br/>例: 『<code>"受注"</code> データをインポートする場合に、取り込み列 <code>"受注番号"</code> の値の重複をチェックして レコードが存在するのであれば 更新、存在しない場合は新規登録』 する設定を記載可能です。 </HelpDoc></div>`,
      ],
      groupKey: 'convertSettings',
      minValueLength: 0,
      columns: {
        dataCol: {
          type: 'STRING',
          label: '取込データ列名',
          validate: { notEmpty: true },
          inputAttrs: { wrapperClass: 'col-2' },
          selections(record, currentValue, initialValue, recordRoot) {
            return [''].concat((recordRoot.fields || []).map((f) => f.dataCol))
          },
          inputHelpText: '直接指定も可能です。',
        },
        targetCol: {
          type: 'STRING',
          label: '重複チェック対象カラム',
          validate: { notEmpty: true },
          dynamicSelections: true,
          selections(record, currentValue, initialValue, recordRoot, callerVueInstance) {
            const parentRecord = recordRoot || {}
            const targetModelName = parentRecord.targetModelName
            if (!targetModelName) {
              return []
            }
            return $core.$models[targetModelName].colNames
          },
        },
        // matchType: {
        //   type: 'SELECT',
        //   label: '照合方法',
        //   selections: () => [
        //     { value: '_eq', label: '完全一致' },
        //     { value: '_starts_with', label: '前方一致' },
        //     { value: '_ends_with', label: '後方一致' },
        //     { value: '_contains', label: '部分一致' },
        //   ],
        //   default: '_eq',
        //   inputAttrs: { wrapperClass: 'col-2' },
        // },
      },
      width: { xs: 48 },
    },
    metaData: {
      type: 'JSON',
      visible: false,
    },
  },
  modelType: 'admin',
}

export const dataImportSettings = model

// action to import data
